<template>
    <Layout>
        <template v-slot:title>
            <div v-if="showTitle">
                <h1>User Quiz</h1>
                <div class="pt-2 text-red text-bold">Records found: {{ totalItems }}</div>
            </div>
            <slot name="header"></slot>
        </template>

        <template v-slot:content>
            <Loader v-if="isLoading"></Loader>
            <div class="row mb-4">
                <div class="col-sm-6 col-md-4 col-xl-2 mb-2 mb-sm-0">
                    <select class="form-control" v-model="filterType">
                        <option value="">Filter by type</option>
                        <option value="poll">Poll</option>
                        <option value="quiz">Quiz</option>
                    </select>
                </div>
                <div class="col-sm-6 col-md-4 col-xl-2 mb-2 mb-md-0">
                    <TagSelect v-model="filterTag" :tag-list="tagList" @input="onFilterTagHandler" />
                </div>
                <!-- school filter -->
                <div v-if="isSchool()" class="col-sm-6 col-md-4 col-xl-2 mb-2 mb-sm-2">
                    <select class="form-control" v-model="filterSubject" @change="onChangeSubject">
                        <option value="">Filter by subject</option>
                        <option v-for="item in school.subjects" :key="item.name" :value="item.name">
                            {{ item.label }}
                        </option>
                    </select>
                </div>
                <div v-if="isSchool()" class="col-sm-6 col-md-4 col-xl-2 mb-2 mb-md-2">
                    <GradeSelect placeholder="Filter by grade" v-model="grades" @select="onFilterGrade"></GradeSelect>
                </div>
                <!-- /school filter -->
                <div class="col-sm-6 col-md-4 col-xl-2 mb-2 mb-md-2">
                    <form @submit.prevent="onSearch" class="input-group btn-inline">
                        <input
                            v-model="keyword"
                            name="table_search"
                            class="form-control float-right"
                            placeholder="Search"
                            type="text"
                        />

                        <div class="input-group-append">
                            <button type="submit" class="btn btn-default">
                                <f-icon icon="search" />
                            </button>
                        </div>
                    </form>
                </div>
                <div class="col-sm-6 col-md-4 col-xl-2">
                    <div
                        v-tippy="{ placement: 'top' }"
                        @click="resetFilter"
                        content="Reset filter"
                        style="min-width: 0px; padding: 0.28rem 1rem"
                        class="btn btn-sm btn-secondary text-lg"
                    >
                        <i class="fas fa-eraser"></i>
                    </div>
                </div>
            </div>

            <div v-if="list.length > 0">
                <div class="row">
                    <div class="col-12">
                        <div class="table-responsive tbq-table-responsive">
                            <table class="table tbq-table">
                                <thead>
                                    <tr>
                                        <th>Image</th>
                                        <th>
                                            <SortLink sort-key="title">Title</SortLink>
                                        </th>
                                        <th>
                                            <SortLink sort-key="playing_counter">Playing count</SortLink>
                                        </th>
                                        <th>Tag</th>
                                        <th>User</th>
                                        <th class="text-center">
                                            <SortLink sort-key="type">Type</SortLink>
                                        </th>
                                        <th class="text-center">
                                            <SortLink sort-key="question_count">Questions</SortLink>
                                        </th>
                                        <th class="text-center">Action</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr v-for="(item, index) in list" :key="index">
                                        <td class="thin-cell text-center align-middle" nowrap>
                                            <router-link
                                                tag="a"
                                                :title="item.title"
                                                :to="{
                                                    name: routeNameQuizDetail,
                                                    params: schoolId
                                                        ? {
                                                              id: schoolId,
                                                              quizId: item._id.$oid
                                                          }
                                                        : {
                                                              id: item._id.$oid
                                                          }
                                                }"
                                            >
                                                <div
                                                    class="bg-image"
                                                    :style="`background-image: url(${getQuizImage(item, true)})`"
                                                ></div>
                                            </router-link>
                                        </td>
                                        <td class="align-middle" style="min-width: 200px; max-width: 250px">
                                            <router-link
                                                tag="a"
                                                :title="item.title"
                                                :to="{
                                                    name: routeNameQuizDetail,
                                                    params: schoolId
                                                        ? {
                                                              id: schoolId,
                                                              quizId: item._id.$oid
                                                          }
                                                        : {
                                                              id: item._id.$oid
                                                          }
                                                }"
                                                class="text-bold"
                                            >
                                                <span>{{ item.title }} </span>
                                                <CheckCircleIcon
                                                    v-if="item.approval_status === 'approved'"
                                                    class="bg-success rounded-circle ml-1"
                                                    style="margin-top: -3px; padding: 2px"
                                                    size="1.2x"
                                                ></CheckCircleIcon>
                                                <XCircleIcon
                                                    v-if="item.approval_status === 'rejected'"
                                                    class="bg-danger rounded-circle ml-1"
                                                    style="margin-top: -3px; padding: 2px"
                                                    size="1.2x"
                                                ></XCircleIcon>
                                            </router-link>
                                            <small class="d-block mb-0">
                                                <span class="font-weight-bold">Limit player: </span>
                                                <span>{{ item.limit_player }}</span>
                                            </small>
                                            <div class="d-flex" style="column-gap: 8px">
                                                <small
                                                    v-if="item.show_school_list"
                                                    v-tippy="{ placement: 'top' }"
                                                    content="Shared in school"
                                                    class="d-block mb-0 cursor-pointer"
                                                >
                                                    <span class="fas fa-school"></span>
                                                </small>
                                                <small
                                                    v-if="item.show_school_country_list"
                                                    v-tippy="{ placement: 'top' }"
                                                    content="Shared in school's country"
                                                    class="d-block mb-0 cursor-pointer"
                                                >
                                                    <span class="fas fa-flag"></span>
                                                </small>
                                            </div>
                                            <small
                                                v-if="isSchool() && getSubjectLabel(item.class_subject)"
                                                class="d-block mb-0"
                                            >
                                                <span class="font-weight-bold">Subject: </span>
                                                <span>{{ getSubjectLabel(item.class_subject) }}</span>
                                            </small>
                                            <small
                                                v-if="isSchool() && getGradeLabel(item.class_grade)"
                                                class="d-block mb-0"
                                            >
                                                <span class="font-weight-bold">Grade: </span>
                                                <span>{{ getGradeLabel(item.class_grade) }}</span>
                                            </small>
                                            <p v-if="item.show_in_front_page" class="text-green mb-0">
                                                Show in front page
                                            </p>
                                        </td>
                                        <td class="align-middle text-center" style="min-width: 200px; max-width: 250px">
                                            <small class="d-block mb-0">
                                                {{
                                                    !isNaN(toNumber(item.playing_counter))
                                                        ? toNumber(item.playing_counter)
                                                        : 0
                                                }}
                                            </small>
                                        </td>
                                        <td class="align-middle">
                                            <Tags :tags="item.tags_name"></Tags>
                                        </td>
                                        <td class="align-middle">
                                            <div v-if="checkUserByQuiz(item.user)">
                                                <p class="mb-1 d-flex">
                                                    <UserIcon class="mr-2" size="1.2x" />
                                                    <a href="#" @click.prevent="filterByUser(item.user._id.$oid)">
                                                        {{ item.user.name }}
                                                    </a>
                                                </p>
                                                <p class="mb-1 d-flex">
                                                    <MailIcon class="mr-2" size="1.2x" /> {{ item.user.email }}
                                                </p>
                                            </div>
                                            <div v-else-if="!item.user.name && !item.user.email" class="text-gray">
                                                [Deleted User]
                                            </div>
                                            <div v-else>
                                                {{ item.email }}
                                            </div>
                                        </td>
                                        <td class="align-middle text-center text-capitalize">
                                            <span
                                                class="px-3 py-1 rounded-pill"
                                                :class="{
                                                    'bg-info': item.type === 'poll',
                                                    'bg-success': item.type === 'quiz'
                                                }"
                                            >
                                                {{ item.type }}
                                            </span>
                                        </td>
                                        <td class="thin-cell text-center align-middle" nowrap>
                                            {{ item.sumQuestion }}
                                        </td>
                                        <td class="thin-cell text-center align-middle" nowrap>
                                            <CopyIcon
                                                @click="setQuizId(item._id.$oid)"
                                                class="text-warning cursor-pointer mr-3"
                                                data-toggle="modal"
                                                data-target="#duplicate-quiz-modal"
                                                size="1.3x"
                                            ></CopyIcon>
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </div>
                    <div class="col-12 my-3">
                        <Paginator
                            @change="onPageChange"
                            :current-page="currentPage"
                            :page-count="pageCount"
                            :total-items="totalItems"
                            ref="setPage"
                        />
                    </div>
                </div>
            </div>
            <EmptyList v-else :label="emptyLabel" />
            <DuplicateQuizModal @duplicate="duplicateQuiz($event)" id="duplicate-quiz-modal" ref="duplicateQuizModal" />
        </template>
    </Layout>
</template>

<script>
import helperMixin from '@/mixins/helper'
import Layout from '@/components/Layout'
import Loader from '@/components/Loader'
import EmptyList from '@/components/EmptyList'
import $ from 'jquery'
import Url from '@/service/url'
import Paginator from '@/components/Paginator'
import { UserIcon, MailIcon, CheckCircleIcon, XCircleIcon, CopyIcon } from 'vue-feather-icons'
import SortLink from '@/components/SortLink'
import Tags from '@/components/Tags'
import TagSelect from '@/components/TagSelect'
import GradeSelect from '@/components/GradeSelect'
import { mapGetters } from 'vuex'
import datalist from '../../../../../frontend/src/mixins/datalist'
import DuplicateQuizModal from '@/components/quizzes/DuplicateQuizModal'

export default {
    name: 'quiz-user-index',

    mixins: [helperMixin, datalist],

    props: {
        showTitle: {
            type: Boolean,
            default: true
        },

        findManyActionName: {
            type: String,
            default: 'quiz/userQuizFindMany'
        },

        routeNameQuizDetail: {
            type: String,
            default: 'quizzes.user.questions'
        },

        schoolId: {
            type: String,
            default: ''
        },

        school: {
            type: Object,
            default: () => ({})
        }
    },

    components: {
        Layout,
        Loader,
        EmptyList,
        GradeSelect,
        Paginator,
        UserIcon,
        MailIcon,
        SortLink,
        Tags,
        TagSelect,
        CheckCircleIcon,
        XCircleIcon,
        CopyIcon,
        DuplicateQuizModal
    },

    created() {
        this.fetchTag().then(() => this.findMany())
    },

    data() {
        return {
            list: [],
            isLoading: false,
            keyword: '',
            filterType: '',
            filterSubject: '',
            filterTag: [],
            filterGrade: [],

            currentPage: 1,
            pageCount: 0,
            totalItems: 0,

            emptyLabel: 'No data in list.',
            tagList: [],
            grades: []
        }
    },

    computed: {
        ...mapGetters('tag', {
            tags: 'tags'
        })
    },

    watch: {
        $route() {
            const url = new Url()

            this.selectedStatus = ''

            this.findMany(url.buildQuery())
            const query = url.getQuery()

            if (!query.tag) {
                this.filterTag = []
            }
        },

        // keyword (value) {
        //     let keyword = value

        //     if (value == "") {
        //         this.$router.replace({
        //             name: this.$router.currentRoute.name,
        //             query: { keyword }
        //         })
        //         .catch(() => {
        //         })
        //     }
        // },

        filterType() {
            this.onSearch()
        },

        tags(val) {
            let tags = val
            let newTags = []

            for (let x in tags) {
                newTags.push({
                    label: tags[x].name,
                    code: tags[x]._id.$oid,
                    type: tags[x].type
                })
            }

            this.tagList = newTags
        }
    },

    methods: {
        findMany() {
            const url = new Url()
            const query = url.buildQuery()
            const self = this

            self.isLoading = true

            self.$store
                .dispatch(this.findManyActionName, { id: this.$router.currentRoute.params.id, query })
                .then(res => {
                    const quiz = res.data.data
                    self.isLoading = false
                    self.list = quiz.data

                    self.pageCount = quiz.page_count
                    self.totalItems = quiz.total
                    self.currentPage = parseFloat(quiz.current_page)
                    self.keyword = query.keyword
                    self.filterType = query.type ? query.type : ''

                    self.list.map(item => {
                        item.sumQuestion = item.question_count
                    })

                    if (query.tag) {
                        self.filterTag = Array.isArray(query.tag) ? query.tag : [query.tag]
                    }

                    if (self.keyword !== '' && self.list.length === 0) {
                        this.emptyLabel = `
                            <p class="text-center text-muted">Your search <b class="text-dark">"${this.keyword}"</b> did not match.</p>
                            <p class="text-center text-muted">Please, try another keyword</p>
                        `
                    }
                })
                .catch(() => {
                    self.isLoading = false
                    // window.shout.error('Quiz not found')
                })
        },

        onSearch() {
            const url = new Url()
            let query

            query = url.buildQuery({
                page: 1,
                keyword: this.keyword,
                type: this.filterType,
                tag: this.filterTag
            })

            if (this.isSchool()) {
                query = url.buildQuery({
                    page: 1,
                    keyword: this.keyword,
                    type: this.filterType,
                    tag: this.filterTag,
                    subject: this.filterSubject,
                    grade: this.filterGrade
                })
            }

            if (this.list.length != 0) {
                this.$refs.setPage.setPage(0)
            }

            this.$router.replace({
                name: this.$router.currentRoute.name,
                query
            })
        },

        onPageChange(page) {
            var url = new Url()

            this.$router.replace({
                name: this.$router.currentRoute.name,
                query: url.buildQuery({ page })
            })
        },

        fetchTag() {
            this.isLoading = true
            return this.$store.dispatch('tag/findAll', { type: 'quiz' })
        },

        filterByUser(id) {
            var url = new Url()
            var query = url.getQuery()

            if (query.user_id == id) {
                return
            }

            if (this.list.length != 0) {
                this.$refs.setPage.setPage(0)
            }

            this.$router.replace({
                name: this.$router.currentRoute.name,
                query: url.buildQuery({ page: 1, user_id: id })
            })
        },

        onFilterTagHandler() {
            this.onSearch()
        },

        resetFilter() {
            this.filterTag = []
            this.filterGrade = []
            this.grades = []
            this.filterType = ''
            this.filterSubject = ''
            this.keyword = ''
            if (this.list.length != 0) {
                this.$refs.setPage.setPage(0)
            }
            this.$router.replace({ name: this.$router.currentRoute.name })
        },

        checkUserByQuiz(user) {
            return user && user.name && user.email
        },

        isSchool() {
            if (this.$route.path.indexOf('school') > -1) {
                return true
            } else {
                return false
            }
        },

        onFilterGrade(e) {
            this.grades = e
            this.filterGrade = e.map(item => item.value)
            this.onSearch()
        },

        onChangeSubject() {
            this.onSearch()
        },

        getGradeLabel(data) {
            const grades = []

            for (let index in data) {
                grades.push(data[index].label)
            }

            grades.splice(0, 0)
            return grades.toString()
        },

        getSubjectLabel(data) {
            if (data) {
                return data.label
            }
        },

        async duplicateQuiz(data) {
            let quiz = await this.$store.dispatch('quiz/findById', data.quizId)
            if (quiz.status === 200) {
                quiz = quiz.data.data

                delete quiz._id
                delete quiz.code
                delete quiz.created_at
                delete quiz.updated_at
                delete quiz.user

                quiz.title = `${quiz.title.replace('(copy)', '')} (copy)`
                quiz.is_pin_quiz = 0
                quiz.is_sponsor_quiz = false
                quiz.playing_counter = 0
                quiz.show_in_front_page = false
                quiz.status = true
                quiz.translate = data.translate
                quiz.duplicate = data.duplicate
                quiz.duplicate_quiz_user = true

                this.$nextTick(async () => {
                    const createQuiz = await this.$store.dispatch('quiz/create', quiz)
                    if (createQuiz.status === 201) {
                        this.findMany()
                        $(`#duplicate-quiz-modal`).modal('hide')

                        window.shout.success('Duplicate quiz success.')
                    } else {
                        window.shout.error('Something wrong!')
                        $(`#duplicate-quiz-modal`).modal('hide')
                    }
                })
            }
        },

        setQuizId(id) {
            this.$refs.duplicateQuizModal.setId(id)
        }
    }
}
</script>

<style lang="scss" scoped>
.col-xl-2 {
    @media (min-width: 1600px) {
        -webkit-box-flex: 0;
        -ms-flex: 0 0 20%;
        flex: 0 0 20%;
        max-width: 20%;
    }
    @media (min-width: 1200px) and (max-width: 1600px) {
        -webkit-box-flex: 0;
        -ms-flex: 0 0 25%;
        flex: 0 0 25%;
        max-width: 25%;
    }
}

.bg-image {
    width: 80px;
    height: 80px;
    border: 3px solid white;
    background-size: cover;
    background-position: center;
    background-repeat: no-repeat;
    border-radius: 0.25rem;
    box-shadow: 0 0 15px rgba(0, 0, 0, 0.3);
}

.theme-dark-mode {
    .bg-image {
        border-color: var(--dark-theme-primary);
    }
}
</style>
